import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_CARD_PROGRAMS } from "graphql/cardProgram";
import { GET_USER } from "graphql/user";
import ApplicationType from "enums/ApplicationType";
import BankLinkFlow from "enums/BankLinkFlow";
import CardHolderType from "enums/CardHolderType";
import CardIssuer from "enums/CardIssuer";
import CardProgramStatus from "enums/CardProgramStatus";
import { Status } from "enums/Company";

export interface IssuerConnection {
  id: string;
  issuer?: CardIssuer;
  nickname?: string;
  status?: CardProgramStatus;
}

/** What can be done with a program: its issuer's support and the company's plan, answered together. */
export interface CardProgramCapabilities {
  bankLinkFlow: BankLinkFlow;
  /** Epoch milliseconds. */
  latestCardExpiration: number;
  moveFunds: boolean;
  /** Wiring money to the company's own bank; only where the issuer sends wires and charges a set fee. */
  sendWire: boolean;
  /** What each wire costs, in the program's currency; null when `sendWire` is false. */
  wireFee?: number | null;
  chargeRepayment: boolean;
  statements: boolean;
  physicalCards: boolean;
  transactionLimit: boolean;
  dailyLimit: boolean;
  weeklyLimit: boolean;
  monthlyLimit: boolean;
  totalLimit: boolean;
  merchantLimits: boolean;
}

export interface CardProgram {
  id: string;
  issuer?: CardIssuer;
  nickname?: string;
  subAccountType?: ApplicationType;
  status?: CardProgramStatus;
  isDefault: boolean;
  last4?: string;
  /** Who this program's new cards go to by default. */
  cardHolderType?: CardHolderType;
  capabilities?: CardProgramCapabilities;
  connection?: IssuerConnection;
  actionRequired?: boolean;
}

export interface UseCardProgramsResult {
  programs: CardProgram[];
  activePrograms: CardProgram[];
  defaultProgram?: CardProgram;
  /** More than one live program, which is what every multi-program surface keys off. */
  multiple: boolean;
  loading: boolean;
  /** Whether the programs are known yet; before that, an empty list means nothing. */
  loaded: boolean;
  refetch: () => void;
}

const LIVE_STATUSES = [
  CardProgramStatus.ACTIVE,
  CardProgramStatus.DISCONNECTED,
];

/**
 * The server's default: of the live programs, the flagged one or the only one.
 * A disconnected default stays the default rather than handing its work to
 * another issuer's program.
 */
export const pickDefaultProgram = (
  programs: CardProgram[],
): CardProgram | undefined => {
  const live = programs.filter(
    (program) => program.status && LIVE_STATUSES.includes(program.status),
  );
  return (
    live.find((program) => program.isDefault) ??
    (live.length === 1 ? live[0] : undefined)
  );
};

const useCardPrograms = (skip = false): UseCardProgramsResult => {
  // The query is only served to an active company, and a platform page has
  // none, so asking there is a rejection the caller cannot use.
  const { data: userData } = useQuery(GET_USER, { fetchPolicy: "cache-first" });
  const isActiveCompany = userData?.user?.company?.status === Status.Active;

  // A program pending when first fetched is approved later, so each mount asks again.
  const { data, loading, refetch } = useQuery(GET_CARD_PROGRAMS, {
    fetchPolicy: "cache-and-network",
    skip: skip || !isActiveCompany,
  });

  return useMemo(() => {
    const programs: CardProgram[] = data?.getCardPrograms ?? [];
    const activePrograms = programs.filter(
      (program) => program.status === CardProgramStatus.ACTIVE,
    );

    return {
      programs,
      activePrograms,
      defaultProgram: pickDefaultProgram(programs),
      multiple: activePrograms.length > 1,
      loading,
      loaded: Boolean(data) || (Boolean(userData) && !isActiveCompany),
      refetch,
    };
  }, [data, loading, refetch, userData, isActiveCompany]);
};

export default useCardPrograms;
